import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Input, Popconfirm, Space, Table, Tag, Typography, message } from 'antd';
import { DownloadOutlined, PlusOutlined, UploadOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { EmployerDTO } from '@/shared/api/model';
import { useSearchEmployers } from '@/shared/api/client/employer-controller';
// import { AXIOS_INSTANCE } from '@/shared/api/mutator';

// Stub for missing AXIOS_INSTANCE
const AXIOS_INSTANCE: any = {};
import { EmployerFormModal } from '../components/EmployerFormModal';
import { EmployerImportModal } from '../components/EmployerImportModal';

const { Title, Text } = Typography;

const toCsv = (rows: EmployerDTO[]) => {
    const headers = ['id', 'name', 'voteNumber', 'address', 'poBox', 'phone', 'email'];
    const escape = (value: any) => {
        const s = (value ?? '').toString();
        if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) {
            return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
    };

    const lines = [headers.join(',')];
    rows.forEach((r) => {
        lines.push(headers.map((h) => escape((r as any)[h])).join(','));
    });

    return lines.join('\n');
};

const downloadCsv = (filename: string, csv: string) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

export const EmployerManagementView: React.FC = () => {
    const [searchText, setSearchText] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedEmployer, setSelectedEmployer] = useState<EmployerDTO | null>(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isImportOpen, setIsImportOpen] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchText), 350);
        return () => clearTimeout(t);
    }, [searchText]);

    const { data, isLoading, refetch, isFetching } = useSearchEmployers(
        { q: debouncedSearch.trim().length > 0 ? debouncedSearch : ' ', limit: 200 },
        {
            query: {
                staleTime: 30_000,
            }
        }
    );

    const employers: EmployerDTO[] = useMemo(() => {
        const first = data?.data ?? data;
        return (first?.data ?? []) as EmployerDTO[];
    }, [data]);

    const setEnabled = async (employer: EmployerDTO, enabled: boolean) => {
        if (!employer?.id) {
            message.error('Employer id is missing');
            return;
        }

        try {
            await AXIOS_INSTANCE.put(`/frontend/v1/employers/${employer.id}/${enabled ? 'enable' : 'disable'}`);
            message.success(enabled ? 'Employer enabled' : 'Employer disabled');
            refetch();
        } catch (e: any) {
            const apiMessage = e?.response?.data?.message;
            message.error(apiMessage || 'Failed to update employer status');
        }
    };

    const deleteEmployer = async (employer: EmployerDTO) => {
        if (!employer?.id) {
            message.error('Employer id is missing');
            return;
        }

        try {
            await AXIOS_INSTANCE.delete(`/frontend/v1/employers/${employer.id}`);
            message.success('Employer deleted');
            refetch();
        } catch (e: any) {
            const apiMessage = e?.response?.data?.message;
            message.error(apiMessage || 'Failed to delete employer');
        }
    };

    const columns: ColumnsType<EmployerDTO> = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (v) => v || '—'
        },
        {
            title: 'Status',
            dataIndex: 'enabled',
            key: 'enabled',
            width: 110,
            render: (v) => {
                const enabled = v !== false;
                return enabled ? <Tag color="green">Enabled</Tag> : <Tag color="red">Disabled</Tag>;
            }
        },
        {
            title: 'Vote Number',
            dataIndex: 'voteNumber',
            key: 'voteNumber',
            render: (v) => v || '—'
        },
        {
            title: 'Address',
            dataIndex: 'address',
            key: 'address',
            render: (v) => v || '—'
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
            render: (v) => v || '—'
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: (v) => v || '—'
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 220,
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => {
                            setSelectedEmployer(record);
                            setIsFormOpen(true);
                        }}
                    />
                    {record.enabled !== false ? (
                        <Popconfirm
                            title="Disable employer"
                            description="This will hide the employer from self-registration search."
                            okText="Disable"
                            cancelText="Cancel"
                            onConfirm={() => setEnabled(record, false)}
                        >
                            <Button danger>Disable</Button>
                        </Popconfirm>
                    ) : (
                        <Popconfirm
                            title="Enable employer"
                            description="This will make the employer available for self-registration search."
                            okText="Enable"
                            cancelText="Cancel"
                            onConfirm={() => setEnabled(record, true)}
                        >
                            <Button type="primary">Enable</Button>
                        </Popconfirm>
                    )}
                    <Popconfirm
                        title="Delete employer"
                        description="This will permanently delete the employer."
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                        cancelText="Cancel"
                        onConfirm={() => deleteEmployer(record)}
                    >
                        <Button danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const handleExport = () => {
        if (employers.length === 0) {
            message.warning('No employers to export');
            return;
        }
        const csv = toCsv(employers);
        downloadCsv('employers-export.csv', csv);
    };

    const handleSaved = () => {
        setIsFormOpen(false);
        setSelectedEmployer(null);
        refetch();
    };

    const handleImported = () => {
        setIsImportOpen(false);
        refetch();
    };

    return (
        <div>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div>
                    <Title level={3} style={{ marginBottom: 0 }}>Employers</Title>
                    <Text type="secondary">Create, update, import and export employers. Vote number must be unique.</Text>
                </div>

                <Card>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                        <Input
                            placeholder="Search by name or vote number"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ width: 360 }}
                            allowClear
                        />

                        <Space wrap>
                            <Button icon={<UploadOutlined />} onClick={() => setIsImportOpen(true)}>
                                Import
                            </Button>
                            <Button icon={<DownloadOutlined />} onClick={handleExport}>
                                Export
                            </Button>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => {
                                    setSelectedEmployer(null);
                                    setIsFormOpen(true);
                                }}
                            >
                                New Employer
                            </Button>
                        </Space>
                    </Space>

                    <div style={{ marginTop: 16 }}>
                        <Table
                            rowKey={(r) => String(r.id ?? `${r.name}-${r.voteNumber ?? ''}`)}
                            columns={columns}
                            dataSource={employers}
                            loading={isLoading || isFetching}
                            pagination={{ pageSize: 10 }}
                        />
                    </div>
                </Card>
            </Space>

            <EmployerFormModal
                open={isFormOpen}
                employer={selectedEmployer}
                onClose={() => {
                    setIsFormOpen(false);
                    setSelectedEmployer(null);
                }}
                onSaved={handleSaved}
            />

            <EmployerImportModal
                open={isImportOpen}
                onClose={() => setIsImportOpen(false)}
                onImported={handleImported}
            />
        </div>
    );
};
